
import { defineComponent, computed, onMounted, ref, onUnmounted, ComponentPublicInstance } from "vue";
import {
  FridgeDetailsCoolingAssistSettingList,
  FridgeDetailsSetSettingsList,
  FRIDGE_DETAILS_COOLING_MODE,
  FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING,
  FridgeCoolingAssistSettingItem
} from "@/types/fridgeDetails";
import NativeClient, { TitleInfo } from "@/client/native-client";
import store from "@/store";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil, { translateYYYYMMDDToJapan } from "@/utils/common-utils";
import HorizontalPaging from "@/components/common/HorizontalPaging.vue";
import FridgeDetailsCoolingAssistSettingsItem from "@/components/fridgeDetails/FridgeDetailsCoolingAssistSettingsItem.vue";
import FridgeDetailsConfirmCoolingAssistSettingDialog from "@/components/fridgeDetails/FridgeDetailsConfirmCoolingAssistSettingDialog.vue";
import FridgeDetailsSelectDateBar from "@/components/fridgeDetails/FridgeDetailsSelectDateBar.vue";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";

export default defineComponent({
name: "FridgeCoolingAssistSettingsPage",
components: { 
  HorizontalPaging,
  FridgeDetailsCoolingAssistSettingsItem,
  FridgeDetailsConfirmCoolingAssistSettingDialog,
  FridgeDetailsSelectDateBar,
  CommonDesignDialog
},
setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "クーリングアシスト"));
    CommonUtil.scroll({ top: 0, left: 0 });

    // 定数
    const modeList = [FRIDGE_DETAILS_COOLING_MODE.QUENCH, FRIDGE_DETAILS_COOLING_MODE.COLD, FRIDGE_DETAILS_COOLING_MODE.FROZEN];
    const modeNameList = [
      FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.QUENCH,
      FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.COLD,
      FRIDGE_DETAILS_COOLING_MODE_DISPLAY_STRING.FROZEN
    ];
    const descriptionList = ["料理や食材を短時間で冷ましたいとき", "短時間の下ごしらえでおいしく料理したいとき", "食材のおいしさを閉じこめて冷凍したいとき"];
    const noteList = ["目安時間：弁当のあら熱とり　3~5分", "目安時間：食材の味のしみこみ　15分", "目安時間：あつあつ食材冷凍　45分"];
    const secondValidList = [true, false, false];
    const isDisableButtonHidden = true;

    // 表示中の位置Index
    const selectPageIndex = ref(1);
    const selectPage = (index: number) => {
      selectPageIndex.value = index;
    }
    const initialPageIndex = ref(0);

    // 上部
    const displayType = ref(1);
    const functionList = ["冷ます", "急冷", "急凍"];
    const changePageIndex = (index: number) => {
      NativeClient.sendDebugLog("FridgeCoolingAssistSettingsPage changePageIndex index = " + index);
      selectPageIndex.value = index;
    };

    class FrudgeDetailsCoolingAssistSetingsItem {
      index: number;                // 添字
      modeName: string;             // モード
      descriptionText: string;      // 説明文
      note: string;                 // 目安時間等
      isSecondsValid: boolean;      // 秒有効／無効
      initialMinutes: number;       // 分初期値
      initialSeconds: number;       // 秒初期値
      minMinutes: number;           // 分最小値
      maxMinutes: number;           // 分最大値
      minSeconds: number;           // 秒最小値
      maxSeconds: number;           // 秒最大値

      constructor() {
        this.index = 0;
        this.modeName = "";
        this.descriptionText = "";
        this.note = "";
        this.isSecondsValid = false;
        this.initialMinutes = 0;
        this.initialSeconds = 0;
        this.minMinutes = 0;
        this.maxMinutes = 0;
        this.minSeconds = 0;
        this.maxSeconds = 0;
      }
    }
    const list = computed(() => {
      const data: FridgeDetailsCoolingAssistSettingList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      const result: Array<FrudgeDetailsCoolingAssistSetingsItem> = [];
      if (data != null) {
        for (let index = 0; index < modeList.length; index ++) {
          const dataIndex = data.cooloven_setting_list.findIndex(element => element.mode == modeList[index]);
          if (dataIndex != -1) {
            const item = new FrudgeDetailsCoolingAssistSetingsItem();
            item.index = index;
            item.modeName = modeNameList[index];
            item.descriptionText = descriptionList[index];
            item.note = noteList[index];
            item.isSecondsValid = secondValidList[index] && data.cooloven_in_seconds;
            item.initialMinutes = data.cooloven_setting_list[dataIndex].default_time;
            item.initialSeconds = 0;
            item.minMinutes = data.cooloven_setting_list[dataIndex].lower_limit;
            item.maxMinutes = data.cooloven_setting_list[dataIndex].upper_limit;
            item.minSeconds = data.cooloven_setting_list[dataIndex].lower_limit_second;
            item.maxSeconds = data.cooloven_setting_list[dataIndex].upper_limit_second;

            result.push(item);
          }
        }
      }
      return result;
    });

    onMounted(() => {
      NativeClient.sendDebugLog("FridgeCoolingAssistSettingsPage onMounted()");

      // ページIDは未決定
      // const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, null, []);
      // NativeClient.sendViewedLog(logInfo);
      initialPageIndex.value = 1;
    });

    // 設定艦長ダイアログ
    const isShowCompletion = ref(false);
    const completionDialogType = ref(12);
    const completionDialogTitle = "設定完了しました。"
    const completionDialogMessage = "";
    const completionDialogButtonTitle = "閉じる";
    const completionCloseButtonHandler = () => {
      isShowCompletion.value = false;

      // 家電TOPに更新要求
      NativeClient.updateApplianceTop();

      // 家電トップに戻る
      NativeClient.pageClose();
    };

    //
    // API実行
    //
    const setCoolingAssist = async (mode: string, minutes: number, seconds: number) => {
     const settings: FridgeDetailsCoolingAssistSettingList | null = store.getters["fridgeDetails/getCoolingAssistSettings"];
      if (settings == null) {
        NativeClient.sendDebugLog("FridgeCoolingAssistSettingsPage setCoolingAssist() getCoolingAssistSettings = " + settings);
        return;
      }
      // buffer.cooloven_mode = mode;
      // buffer.cooloven_time = minutes;
      const data = new FridgeCoolingAssistSettingItem();
      data.cooloven_mode = mode;
      data.cooloven_time = minutes;
      if (mode == FRIDGE_DETAILS_COOLING_MODE.QUENCH && settings.cooloven_in_seconds) {
        data.cooloven_second = seconds;
      } else {
        data.cooloven_second = undefined;
      }
      store.dispatch("common/startFullscreenLoading");
      await NativeClient.getApiParams();
      await store.dispatch("fridgeDetails/setCoolingAssist", data);
      store.dispatch("common/stopFullscreenLoading");

      // 完了ダイアログ
      isShowCompletion.value = true;
    };

    // 設定関連
    const isShowConfirmDialog = ref(false);
    const settingModeName = ref("");
    const settingMode = ref("");
    const settingMinutes = ref(0);
    const settingSeconds = ref(0);
    const isValidSeconds = ref(false);
    const selectSetting = (index: number, minutes: number, seconds: number) => {
      NativeClient.sendDebugLog("FridgeCoolingAssistSettingsPage selectSetting(index: " + index + ", minutes: " + minutes + ", seconds: " + seconds + ")");
      if (index == 0) {
        isValidSeconds.value = true;
      } else {
        isValidSeconds.value = false;
      }
      settingModeName.value = modeNameList[index];
      settingMode.value = modeList[index];
      settingMinutes.value = minutes;
      settingSeconds.value = seconds;
      isShowConfirmDialog.value = true;
    };
    const confirmCancel = () => {
      isShowConfirmDialog.value = false;
    };
    const confirmSend = () => {
      isShowConfirmDialog.value = false;

      setCoolingAssist(settingMode.value, settingMinutes.value, settingSeconds.value);
    };

    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const apiErrorTitle = computed(() => {
      if (getApiErrorTitle != undefined && getApiErrorTitle != null && getApiErrorTitle.value != "") {
        return getApiErrorTitle.value;
      }
      return "送信できませんでした";
    });
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      closeDialog();
    };
    const okApiErrorHandler = () => {
      closeDialog();
    };

    return {
      displayType,
      functionList,
      isDisableButtonHidden,
      changePageIndex,
      selectPageIndex,
      initialPageIndex,
      selectPage,
      list,
      isShowConfirmDialog,
      settingModeName,
      settingMinutes,
      settingSeconds,
      isValidSeconds,
      selectSetting,
      confirmCancel,
      confirmSend,
      isShowCompletion,
      completionDialogType,
      completionDialogTitle,
      completionDialogMessage,
      completionDialogButtonTitle,
      completionCloseButtonHandler,
      handleApiError,
      apiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler
    };
  }
});
